import { trackCaptureEvent } from './captureAnalytics';
import { routePattern } from './routePattern';

/**
 * Global JS error capture for the PWA.
 *
 * The PWA had NO error capture of any kind — no `error`/`unhandledrejection`
 * handlers, no error boundary, no SDK. A single exception white-screened the
 * guest acquisition funnel with no signal anywhere, and because there was also
 * no funnel-silence alert, even the resulting silence went unnoticed.
 *
 * Routed through the existing first-party telemetry pipe rather than a vendor
 * SDK — the events land in `monitoring_client_events`, group into `/issues` as
 * synthetic spans, and are readable via the admin `/client-events` endpoint.
 */

/**
 * Stack traces are unbounded, so cap them before they reach the wire.
 *
 * Note where this actually lands: `stack` is passed as metadata, which
 * `trackCaptureEvent` spreads into the event's `attributes` jsonb — NOT into
 * the schema's top-level `errorMessage varchar(512)` column, which no client
 * populates. `attributes` has no length bound at the zod layer; the real
 * server-side cap is `MAX_STRING_LENGTH = 500` in `clientTelemetry.routes.ts`,
 * applied per attribute after ingest.
 *
 * So this budget is about not shipping kilobytes over a metered connection,
 * not about fitting a column — raising it would simply be re-truncated to 500
 * on arrival.
 */
const MAX_STACK_CHARS = 2_000;
const MAX_MESSAGE_CHARS = 300;

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

export function describeError(value: unknown): { message: string; stack?: string; name?: string } {
  if (value instanceof Error) {
    return {
      message: truncate(value.message || value.name || 'Error', MAX_MESSAGE_CHARS),
      stack: value.stack ? truncate(value.stack, MAX_STACK_CHARS) : undefined,
      name: value.name,
    };
  }
  if (typeof value === 'string') return { message: truncate(value, MAX_MESSAGE_CHARS) };
  try {
    return { message: truncate(JSON.stringify(value) ?? 'Unknown error', MAX_MESSAGE_CHARS) };
  } catch {
    return { message: 'Unserializable error' };
  }
}

/**
 * Current route as a PATTERN, never the concrete path.
 *
 * Dropping the query string is not enough on this app: the secret is the path
 * segment. `/join/<inviteToken>`, `/phase-invite/<token>`,
 * `/reset-password/<token>`, `/cancel-deletion/<token>`, `/r/<shareToken>` and
 * `/recap/<shareToken>` all carry a live credential in the URL itself. Sending
 * the raw pathname would write working invite and password-reset tokens into
 * `monitoring_client_events` — where nothing redacts them (the ingest route
 * only truncates strings) and where they surface in the admin dashboard tagged
 * `error`/`fatal`, i.e. the first rows an operator opens.
 *
 * Uses the same `routePattern` as page-view and web-vitals reporting, so all
 * three surfaces redact identically.
 */
export function currentRoute(): string {
  if (typeof window === 'undefined') return 'unknown';
  return routePattern(window.location.pathname);
}

let installed = false;

export function installGlobalErrorHandlers(target: Window = window): void {
  // Idempotent: HMR and tests both re-run module init.
  if (installed) return;
  installed = true;

  target.addEventListener('error', (event: ErrorEvent) => {
    // Resource load failures (a 404'd <img>) surface here too but carry no
    // `error` object; they are noise, not app faults.
    if (!event.error && !event.message) return;
    const described = describeError(event.error ?? event.message);
    trackCaptureEvent('js_error', {
      surface: 'app',
      message: described.message,
      stack: described.stack,
      errorName: described.name,
      route: currentRoute(),
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });

  target.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const described = describeError(event.reason);
    trackCaptureEvent('unhandled_rejection', {
      surface: 'app',
      message: described.message,
      stack: described.stack,
      errorName: described.name,
      route: currentRoute(),
    });
  });
}

/** Test seam. */
export function __resetErrorHandlersForTest(): void {
  installed = false;
}
